/**
 * premiumChartMath.js
 *
 * Pure geometry helpers behind the premium chart kit
 * (`components/premium/PremiumCharts.js`): smooth sparkline paths, bubble
 * packing and compact number formatting. No React / React-Native imports so the
 * Jest suite can assert the maths directly.
 *
 * @module helpers/premiumChartMath
 */

/**
 * Coerce an arbitrary series entry (number or `{value}`) into a finite number.
 *
 * @param {*} entry Raw series entry.
 * @returns {number} A finite number (0 when unusable).
 */
export const toNumber = (entry) => {
    const raw = typeof entry === 'object' && entry !== null ? entry.value : entry;
    const value = Number(raw);

    return Number.isFinite(value) ? value : 0;
};

/**
 * Map a numeric series onto pixel points inside a `width`×`height` box.
 *
 * A flat series is drawn on the vertical centre line instead of collapsing onto
 * an edge, and a single value is stretched into a horizontal line.
 *
 * @param {Array<number|{value: number}>} data Series values.
 * @param {number} width Box width in px.
 * @param {number} height Box height in px.
 * @param {number} [padding=4] Vertical padding in px (keeps the stroke inside the box).
 * @returns {Array<{x: number, y: number}>} Points left → right.
 */
export const seriesToPoints = (data, width, height, padding = 4) => {
    const values = (Array.isArray(data) ? data : []).map(toNumber);

    if (!values.length) {
        return [];
    }

    const list = values.length === 1 ? [values[0], values[0]] : values;
    const max = Math.max(...list);
    const min = Math.min(...list);
    const span = max - min;
    const usable = Math.max(1, height - padding * 2);
    const step = list.length > 1 ? width / (list.length - 1) : width;

    return list.map((value, index) => ({
        x: index * step,
        y: span === 0 ? height / 2 : padding + usable - ((value - min) / span) * usable,
    }));
};

/**
 * Build a smooth cubic-bezier path (Catmull-Rom → Bézier) through the points.
 *
 * @param {Array<{x: number, y: number}>} points Points left → right.
 * @returns {string} SVG path `d` ('' for no points).
 */
export const smoothLinePath = (points) => {
    if (!Array.isArray(points) || !points.length) {
        return '';
    }
    if (points.length === 1) {
        return `M${points[0].x},${points[0].y}`;
    }

    const round = (n) => Math.round(n * 100) / 100;
    let d = `M${round(points[0].x)},${round(points[0].y)}`;

    for (let i = 0; i < points.length - 1; i += 1) {
        const p0 = points[i - 1] || points[i];
        const p1 = points[i];
        const p2 = points[i + 1];
        const p3 = points[i + 2] || p2;
        const c1x = p1.x + (p2.x - p0.x) / 6;
        const c1y = p1.y + (p2.y - p0.y) / 6;
        const c2x = p2.x - (p3.x - p1.x) / 6;
        const c2y = p2.y - (p3.y - p1.y) / 6;

        d += ` C${round(c1x)},${round(c1y)} ${round(c2x)},${round(c2y)} ${round(p2.x)},${round(p2.y)}`;
    }

    return d;
};

/**
 * Close a line path down to the baseline so it can be filled as an area.
 *
 * @param {string} linePath Output of {@link smoothLinePath}.
 * @param {Array<{x: number, y: number}>} points The points the path was built from.
 * @param {number} height Box height (the baseline y).
 * @returns {string} SVG path `d` ('' when there is nothing to close).
 */
export const areaPathFromLine = (linePath, points, height) => {
    if (!linePath || !Array.isArray(points) || points.length < 2) {
        return '';
    }

    const first = points[0];
    const last = points[points.length - 1];

    return `${linePath} L${last.x},${height} L${first.x},${height} Z`;
};

/**
 * Pack circles (area ∝ value) into a `width`×`height` box without overlap.
 *
 * Largest first at the centre, every following circle is placed at the free
 * spot nearest the centroid found by a deterministic spiral search; the result
 * is then uniformly scaled/centred to fill the box. Deterministic, so the same
 * data always renders the same cluster.
 *
 * @param {number[]} values Positive values (zero / negative are dropped).
 * @param {{width: number, height: number, gap?: number, minRatio?: number}} options
 *   Box size, spacing between circles and the smallest allowed radius relative
 *   to the largest (keeps tiny segments visible).
 * @returns {Array<{index: number, x: number, y: number, r: number}>} One entry
 *   per kept value, in the input's index space.
 */
export const packCircles = (values, {width, height, gap = 6, minRatio = 0.34} = {}) => {
    const items = (Array.isArray(values) ? values : [])
        .map((value, index) => ({index, value: toNumber(value)}))
        .filter((item) => item.value > 0)
        .sort((a, b) => b.value - a.value);

    if (!items.length || !(width > 0) || !(height > 0)) {
        return [];
    }

    const maxValue = items[0].value;
    const baseRadius = 100;
    const placed = [];

    items.forEach((item, order) => {
        const ratio = Math.max(minRatio, Math.sqrt(item.value / maxValue));
        const r = baseRadius * ratio;

        if (order === 0) {
            placed.push({index: item.index, x: 0, y: 0, r});

            return;
        }

        const centroid = placed.reduce((acc, c) => ({x: acc.x + c.x / placed.length, y: acc.y + c.y / placed.length}), {x: 0, y: 0});
        let best = null;

        // Spiral outwards from the centroid; the first angle sweep that yields
        // any collision-free spot wins, nearest-to-centroid within that ring.
        for (let dist = 0; dist < 2400 && !best; dist += 4) {
            for (let step = 0; step < 48; step += 1) {
                // Golden-angle offset per ring so candidates don't line up on the axes.
                const angle = (step / 48) * Math.PI * 2 + dist * 0.07;
                const x = centroid.x + Math.cos(angle) * dist;
                const y = centroid.y + Math.sin(angle) * dist * 0.82;
                const free = placed.every((c) => Math.hypot(c.x - x, c.y - y) >= c.r + r + gap);

                if (free && (!best || Math.hypot(x - centroid.x, y - centroid.y) < best.d)) {
                    best = {x, y, d: Math.hypot(x - centroid.x, y - centroid.y)};
                }
            }
        }

        placed.push({index: item.index, x: best ? best.x : 0, y: best ? best.y : 0, r});
    });

    const minX = Math.min(...placed.map((c) => c.x - c.r));
    const maxX = Math.max(...placed.map((c) => c.x + c.r));
    const minY = Math.min(...placed.map((c) => c.y - c.r));
    const maxY = Math.max(...placed.map((c) => c.y + c.r));
    const scale = Math.min(width / (maxX - minX), height / (maxY - minY));
    const offsetX = (width - (maxX - minX) * scale) / 2 - minX * scale;
    const offsetY = (height - (maxY - minY) * scale) / 2 - minY * scale;

    return placed.map((c) => ({
        index: c.index,
        x: c.x * scale + offsetX,
        y: c.y * scale + offsetY,
        r: c.r * scale,
    }));
};

/**
 * Compact label for chart tooltips / axes: `1 234 567` → `1,2M`, `45 300` → `45,3k`.
 *
 * @param {number} value Number to shorten.
 * @param {string} [decimalSeparator=','] Decimal mark (the app default locale is hu).
 * @returns {string} Compact text (`'0'` for non-finite input).
 */
export const compactNumber = (value, decimalSeparator = ',') => {
    const n = Number(value);

    if (!Number.isFinite(n)) {
        return '0';
    }

    const abs = Math.abs(n);
    const trim = (v) => String(Math.round(v * 10) / 10).replace('.', decimalSeparator);

    if (abs >= 1e9) {
        return `${trim(n / 1e9)}Mrd`;
    }
    if (abs >= 1e6) {
        return `${trim(n / 1e6)}M`;
    }
    if (abs >= 1e3) {
        return `${trim(n / 1e3)}k`;
    }

    return String(Math.round(n));
};

/**
 * Share of `part` in `total` as a whole percentage (0 when `total` is empty).
 *
 * @param {number} part Numerator.
 * @param {number} total Denominator.
 * @returns {number} 0..100 integer.
 */
export const percentOf = (part, total) => {
    const denominator = Number(total);

    if (!(denominator > 0)) {
        return 0;
    }

    return Math.max(0, Math.min(100, Math.round((Number(part) / denominator) * 100)));
};
